import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listLessons, getThemes, generateLesson } from "@/lib/api";
import LessonCard from "@/components/LessonCard";
import Loading from "@/components/Loading";
import ScriptureSearch from "@/components/ScriptureSearch";
import { Filter, Sparkles, RotateCw, X } from "lucide-react";
import { toast } from "sonner";

export default function Archive() {
    const [lessons, setLessons] = useState([]);
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [age, setAge] = useState("all");
    const [theme, setTheme] = useState("");
    const [searchActive, setSearchActive] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const bookFilter = searchParams.get("book") || "";

    async function fetchLessons() {
        setLoading(true);
        try {
            const params = {};
            if (age !== "all") params.age_group = age;
            if (theme) params.theme = theme;
            if (bookFilter) params.bible_book = bookFilter;
            const data = await listLessons(params);
            setLessons(data);
            setSearchActive(false);
        } catch (e) {
            toast.error("Couldn't load lessons");
        } finally {
            setLoading(false);
        }
    }

    function clearBookFilter() {
        const next = new URLSearchParams(searchParams);
        next.delete("book");
        setSearchParams(next);
    }

    function handleSearchResults(res) {
        setLessons(res.results);
        setSearchActive(true);
        setSearchQuery(res.query);
    }

    function handleSearchClear() {
        fetchLessons();
        setSearchQuery("");
    }

    useEffect(() => {
        getThemes().then((d) => setThemes(d.themes || [])).catch(() => {});
    }, []);

    useEffect(() => {
        fetchLessons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [age, theme, bookFilter]);

    const grouped = useMemo(() => lessons, [lessons]);

    async function handleGenerate(seedTheme) {
        setGenerating(true);
        try {
            const lesson = await generateLesson(seedTheme ? { theme: seedTheme } : {});
            toast.success(`New lesson: ${lesson.title}`);
            fetchLessons();
        } catch (e) {
            toast.error("Couldn't generate a new lesson right now.");
        } finally {
            setGenerating(false);
        }
    }

    return (
        <div data-testid="archive-page">
            <section className="border-b-2 border-ink bg-sky/30">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
                    <span className="pill bg-white">All Lessons</span>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-3">
                        The Daily Lessons Archive
                    </h1>
                    <p className="text-lg text-ink/80 mt-3 max-w-2xl">
                        Every story, every age, every kind action. Filter by age group or theme — or search by Bible book, reference, or theme.
                    </p>
                    <div className="mt-6">
                        <ScriptureSearch onResults={handleSearchResults} onClear={handleSearchClear} />
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sticky top-[73px] z-30 bg-cream/95 backdrop-blur border-b-2 border-ink/10">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5" strokeWidth={2.5} />
                        <span className="font-heading font-semibold">Filter</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2" data-testid="age-filter">
                            <span className="text-xs uppercase font-bold tracking-widest text-ink/60">Age</span>
                            {[
                                { v: "all", l: "All" },
                                { v: "5-8", l: "5–8" },
                                { v: "9-12", l: "9–12" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setAge(o.v)}
                                    data-testid={`age-${o.v}`}
                                    className={`pill transition-all ${
                                        age === o.v ? "bg-ink text-cream" : "bg-white hover:bg-sun"
                                    }`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-2" data-testid="theme-filter">
                            <span className="text-xs uppercase font-bold tracking-widest text-ink/60">Theme</span>
                            <select
                                value={theme}
                                onChange={(e) => setTheme(e.target.value)}
                                className="border-2 border-ink rounded-full bg-white px-4 py-1.5 font-semibold text-sm focus:outline-none focus:ring-4 focus:ring-sky"
                                data-testid="theme-select"
                            >
                                <option value="">Any theme</option>
                                {themes.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={() => handleGenerate(theme || null)}
                            disabled={generating}
                            data-testid="generate-lesson-btn"
                            className="btn-sticker bg-coral text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {generating ? (
                                <>
                                    <RotateCw className="w-4 h-4 mr-2 animate-spin" strokeWidth={2.5} />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                    Generate a lesson
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                {bookFilter && (
                    <div className="mb-6 flex items-center gap-2 text-sm" data-testid="book-filter-indicator">
                        <span className="pill bg-coral text-white">Book: {bookFilter}</span>
                        <button onClick={clearBookFilter} className="font-heading font-semibold underline inline-flex items-center gap-1">
                            <X className="w-4 h-4" strokeWidth={2.5} /> Clear
                        </button>
                    </div>
                )}
                {searchActive && (
                    <div className="mb-6 flex items-center gap-2 text-sm" data-testid="search-indicator">
                        <span className="pill bg-sun">Search</span>
                        <span className="text-ink/70">{lessons.length} result{lessons.length === 1 ? "" : "s"} for "<strong>{searchQuery}</strong>"</span>
                        <button onClick={handleSearchClear} className="font-heading font-semibold underline">Clear</button>
                    </div>
                )}
                {loading ? (
                    <Loading label="Loading lessons..." />
                ) : grouped.length === 0 ? (
                    <div className="card-sticker p-10 text-center bg-white" data-testid="empty-archive">
                        <h3 className="font-heading text-2xl font-bold">No lessons match yet</h3>
                        <p className="text-ink/70 mt-2">Try a different filter or tap "Generate a lesson" to create one!</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="lessons-grid">
                        {grouped.map((l) => (
                            <LessonCard key={l.id} lesson={l} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
